import React, { useState, useEffect, useMemo } from 'react';
import BBALoader from '../components/BBALoader';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Mail,
  Database,
  X,
  Check,
  MessageSquare,
  Users,
  Zap,
  Globe,
  Smartphone,
  Calendar,
  Link2,
  Search,
  Plus
} from 'lucide-react';
import { collection, getDocs, doc, updateDoc, addDoc, deleteDoc, query, where, orderBy, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import TechnologyPageHeader from '../components/technology/TechnologyPageHeader';
import { useTechnologyEmbedded } from '../hooks/useTechnologyEmbedded';
import { useAuth } from '../contexts/AuthContext';
import { useCompanyId } from '../hooks/useCompanyId';
import { getAllUserAutomationData, saveUserAutomationRequest, getUserAutomationRequests } from '../services/userAutomationService';
import { deleteUserAppConnection } from '../services/userAppConnectionsService';
import TechnologyAutomationCard from '../components/technology/TechnologyAutomationCard';
import { filterResourcesBySearch } from '../services/technologyCommunityService';
import AutomationOverview from './AutomationOverview';
import { getAutomationGroupEmojis, sortAutomationRows } from '../services/automationTemplatesService';
import PortalBackground from '../components/PortalBackground';

// Department groups rendered as jobtread-imports-style sections.
// Default emojis match the CRM automation-templates tabs; CRM-picked
// group emojis override them.
const DEPARTMENT_SECTIONS = [
  { id: 'lead', label: 'Lead', emoji: '🎯' },
  { id: 'design', label: 'Design', emoji: '🎨' },
  { id: 'build', label: 'Build', emoji: '🏗️' },
  { id: 'office', label: 'Office', emoji: '💼' },
];

// overrideCompanyId/overrideUserId let an internal viewer (CRM Portal View)
// load a specific client account's automations instead of their own.
const Automation = ({ embedded: embeddedProp, overrideCompanyId = null, overrideUserId = null }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const embedded = useTechnologyEmbedded(embeddedProp);
  const { currentUser } = useAuth();
  const { companyId: resolvedCompanyId } = useCompanyId();
  const companyId = overrideCompanyId || resolvedCompanyId;
  // The uid whose per-user automation state (app connections) is read/written.
  const effectiveUid = overrideUserId || currentUser?.uid;
  // Internal (CRM Portal View) usage — an override id is only ever set when an
  // internal user views a client. Internal users add automations for the client
  // directly instead of requesting them.
  const isInternal = Boolean(overrideUserId);

  // State for department filter ('all' shows every section)
  const [activeTab, setActiveTab] = useState('all');
  
  // State for automations
  const [automations, setAutomations] = useState([]);
  const [userAutomationRequests, setUserAutomationRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');


  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestForm, setRequestForm] = useState({
    name: '',
    department: 'Lead',
    description: '',
    appsUsed: '',
    softwares: [],
    emojis: [],
    workflowSteps: []
  });

  // State for custom automations
  const [customAutomations, setCustomAutomations] = useState([]);
  const [editingAutomation, setEditingAutomation] = useState(null);

  // State for Connect Apps modal
  const [showConnectAppsModal, setShowConnectAppsModal] = useState(false);
  const [showAppLoginModal, setShowAppLoginModal] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [connectedApps, setConnectedApps] = useState({});
  const [appCredentials, setAppCredentials] = useState({});
  const [userAppConnections, setUserAppConnections] = useState({});
  const [userTechStack, setUserTechStack] = useState({});
  const [showConnectionFieldsModal, setShowConnectionFieldsModal] = useState(false);
  const [selectedConnectionFields, setSelectedConnectionFields] = useState(null);
  const [connectionFieldValues, setConnectionFieldValues] = useState({});
  const [isSavingFields, setIsSavingFields] = useState(false);
  // Automation opened in the details popup (replaces navigating to the overview page)
  const [selectedAutomationId, setSelectedAutomationId] = useState(null);

  // CRM-picked department emojis (override the section defaults)
  const [groupEmojis, setGroupEmojis] = useState({});

  useEffect(() => {
    getAutomationGroupEmojis().then(setGroupEmojis).catch(() => {});
  }, []);

  const [searchParams] = useSearchParams();
  const highlightParam = searchParams.get('highlight');
  const [highlightId, setHighlightId] = useState(null);

  // Deep link (?highlight=<id>) from What's New: scroll to the card and flash a ring.
  useEffect(() => {
    if (!highlightParam) return undefined;
    const scrollTimer = setTimeout(() => {
      const el = document.getElementById(`automation-card-${highlightParam}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightId(highlightParam);
      }
    }, 350);
    const clearTimer = setTimeout(() => setHighlightId(null), 3200);
    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(clearTimer);
    };
  }, [highlightParam, automations]);

  // State for available softwares
  const [availableSoftwares, setAvailableSoftwares] = useState([]);
  const [availableSoftwareItems, setAvailableSoftwareItems] = useState([]); // Individual software items
  const [userAutomationData, setUserAutomationData] = useState({});
  const [dataLoaded, setDataLoaded] = useState(false);

  // State for software collections and workflow steps
  const [softwareCollections, setSoftwareCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState('');
  const [selectedSoftwareType, setSelectedSoftwareType] = useState('');
  const [workflowStepForm, setWorkflowStepForm] = useState({
    title: '',
    description: '',
    order: 1,
    type: 'trigger'
  });

  // Helper function to get connection fields for specific software
  const getSoftwareConnectionFields = (softwareName) => {
    const softwareItem = availableSoftwareItems.find(item => item.name === softwareName);
    return softwareItem?.connectionFields || [];
  };

  // Handler for collection change
  const handleCollectionChange = (collectionName) => {
    setSelectedCollection(collectionName);
    setSelectedSoftwareType(''); // Reset software type when collection changes
  };

  // Handler for software type selection
  const handleSoftwareTypeSelect = () => {
    if (selectedSoftwareType && !requestForm.softwares.includes(selectedSoftwareType)) {
      setRequestForm(prev => ({
        ...prev,
        softwares: [...prev.softwares, selectedSoftwareType]
      }));
      setSelectedSoftwareType('');
    }
  };

  // Handler for removing software type
  const handleRemoveSoftwareType = (softwareTypeId) => {
    setRequestForm(prev => ({
      ...prev,
      softwares: prev.softwares.filter(id => id !== softwareTypeId)
    }));
  };

  // Handler for emoji selection
  const handleEmojiSelect = (emoji) => {
    if (!requestForm.emojis.includes(emoji)) {
      setRequestForm(prev => ({
        ...prev,
        emojis: [...prev.emojis, emoji]
      }));
    }
  };

  // Handler for removing emoji
  const handleRemoveEmoji = (index) => {
    setRequestForm(prev => ({
      ...prev,
      emojis: prev.emojis.filter((_, i) => i !== index)
    }));
  };

  // Handler for adding workflow step
  const handleAddWorkflowStep = () => {
    if (workflowStepForm.title.trim()) {
      const newStep = {
        ...workflowStepForm,
        id: `step_${Date.now()}`,
        order: requestForm.workflowSteps.length + 1
      };
      
      setRequestForm(prev => ({
        ...prev,
        workflowSteps: [...prev.workflowSteps, newStep]
      }));
      
      // Reset form
      setWorkflowStepForm({
        title: '',
        description: '',
        order: requestForm.workflowSteps.length + 2,
        type: 'trigger'
      });
    }
  };

  // Handler for removing workflow step
  const handleRemoveWorkflowStep = (index) => {
    setRequestForm(prev => ({
      ...prev,
      workflowSteps: prev.workflowSteps.filter((_, i) => i !== index)
    }));
  };

  // Get selected apps from automation templates with user's actual software selections
  const getSelectedApps = () => {
    try {
      const savedApps = localStorage.getItem('appConnections');
      console.log('Raw localStorage appConnections:', savedApps);
      if (savedApps && availableSoftwares.length > 0) {
        const selectedAppIds = JSON.parse(savedApps);
        console.log('Parsed selectedAppIds:', selectedAppIds);
        console.log('Available softwares:', availableSoftwares);
        console.log('Available software IDs:', availableSoftwares.map(s => s.id));
        console.log('User tech stack:', userTechStack);
        
        // Map software type IDs to actual software selections
        const mappedApps = selectedAppIds.map(typeId => {
          // Find the software type
          const softwareType = availableSoftwares.find(s => s.id === typeId);
          if (!softwareType) return null;
          
          // Check if user has selected specific software for this type
          const userSelectedSoftware = userTechStack.selectedSoftware?.find(sw => sw.toolId === typeId);
          if (userSelectedSoftware && userSelectedSoftware.name !== 'None') {
            // Return the specific software with connection fields
            return {
              id: typeId, // Keep the type ID for reference
              name: userSelectedSoftware.name,
              type: softwareType.name,
              tier: softwareType.tier,
              description: softwareType.description,
              image: softwareType.image,
              isUserSelection: true,
              // Get connection fields from the specific software
              connectionFields: getSoftwareConnectionFields(userSelectedSoftware.name)
            };
          } else {
            // Return the software type if no specific selection
            return {
              ...softwareType,
              isUserSelection: false
            };
          }
        }).filter(Boolean);
        
        console.log('Mapped apps with user selections:', mappedApps);
        return mappedApps;
      }
      return [];
    } catch (error) {
      console.error('Error parsing saved app connections:', error);
      return [];
    }
  };

  // Fetch automations from Firebase
  useEffect(() => {
    const fetchAutomations = async () => {
      try {
        const automationsRef = collection(db, 'automations');
        const q = query(automationsRef, orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        
        const fetchedAutomations = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            category: data.category || 'lead',
            softwares: data.softwares || [],
            authorName: data.authorName || (data.source === 'community' ? 'Community' : 'BBA Team'),
          };
        });

        // Honor the CRM-managed display order (sortOrder within each category)
        setAutomations(sortAutomationRows(fetchedAutomations));
        setLoading(false);
      } catch (error) {
        console.error('Error fetching automations:', error);
        setLoading(false);
      }
    };

    fetchAutomations();
  }, []);

  // Set dataLoaded when softwares are loaded
  useEffect(() => {
    if (availableSoftwares.length > 0 && availableSoftwareItems.length > 0) {
      setDataLoaded(true);
      // Also check if we have selected apps
      const selectedApps = getSelectedApps();
      console.log('Software loaded, selected apps count:', selectedApps.length);
      
      // Force a re-render to show updated apps
      setDataLoaded(false);
      setTimeout(() => setDataLoaded(true), 100);
    }
  }, [availableSoftwares, availableSoftwareItems]);

  // Listen for localStorage changes to automatically update when apps are selected in AutomationTemplates
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'appConnections') {
        console.log('LocalStorage appConnections changed:', e.newValue);
        // Force a re-render to show updated apps
        setDataLoaded(false);
        setTimeout(() => setDataLoaded(true), 100);
      }
    };

    // Custom event listener for same-tab localStorage changes
    const handleCustomStorageChange = (e) => {
      if (e.detail && e.detail.key === 'appConnections') {
        console.log('Custom storage event appConnections changed:', e.detail.newValue);
        setDataLoaded(false);
        setTimeout(() => setDataLoaded(true), 100);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('localStorageChange', handleCustomStorageChange);
    
    // Also check for changes when the page becomes visible (in case user switches tabs)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('Page became visible, checking for localStorage changes');
        setDataLoaded(false);
        setTimeout(() => setDataLoaded(true), 100);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleCustomStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Additional effect to watch for changes in availableSoftwares and refresh selected apps
  useEffect(() => {
    if (availableSoftwares.length > 0 && availableSoftwareItems.length > 0) {
      console.log('Available softwares changed, refreshing selected apps');
      const selectedApps = getSelectedApps();
      console.log('Current selected apps after refresh:', selectedApps);
    }
  }, [availableSoftwares, availableSoftwareItems]);

  // Fetch user's automation data
  useEffect(() => {
    const fetchUserAutomationData = async () => {
      if (!currentUser) return;
      
      try {
        const userData = await getAllUserAutomationData(effectiveUid, companyId || null);
        setUserAutomationData(userData);
      } catch (error) {
        console.error('Error fetching user automation data:', error);
      }
    };

    fetchUserAutomationData();
  }, [currentUser, companyId, effectiveUid]);

  // Fetch user's automation requests
  useEffect(() => {
    const fetchUserAutomationRequests = async () => {
      if (!currentUser) return;
      
      try {
        const requests = await getUserAutomationRequests(effectiveUid, companyId || null);
        setUserAutomationRequests(requests);
      } catch (error) {
        console.error('Error fetching user automation requests:', error);
      }
    };

    fetchUserAutomationRequests();
  }, [currentUser, companyId, effectiveUid]);

  // Fetch user's app connections and tech stack
  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser) return;
      
      try {
        // Fetch user's tech stack selections and app connections from user document
        const userDoc = await getDoc(doc(db, 'users', effectiveUid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // Get app connections from user document
          const connections = userData.appConnections || {};
          setUserAppConnections(connections);
          
          // Update connectedApps state based on user connections
          const connectedState = {};
          Object.keys(connections).forEach(softwareId => {
            connectedState[softwareId] = true;
          });
          setConnectedApps(connectedState);
          
          // Update appCredentials state based on user connections
          const credentialsState = {};
          Object.entries(connections).forEach(([softwareId, connectionData]) => {
            credentialsState[softwareId] = connectionData.fields || {};
          });
          setAppCredentials(credentialsState);
          
          // Get tech stack
          const userTechStack = userData.techStack || {};
          setUserTechStack(userTechStack);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, [currentUser, effectiveUid]);

  // Fetch available softwares from technologyStack collection
  useEffect(() => {
    const fetchSoftwares = async () => {
      try {
        const collectionNames = ['Core', 'Primary', 'Secondary'];
        const softwareList = [];
        const softwareItemsList = [];
        const collections = [];

        for (const collectionName of collectionNames) {
          const typesSnapshot = await getDocs(collection(db, 'technologyStack', collectionName, 'types'));
          const types = [];
          
          for (const typeDoc of typesSnapshot.docs) {
            const typeData = typeDoc.data();
            if (typeData && typeData.name) {
              const softwareType = {
                id: typeDoc.id, // This is the ID that AutomationTemplates stores
                name: typeData.name,
                type: typeData.name,
                tier: collectionName,
                description: typeData.description || '',
                image: typeData.image || '',
                suggested: typeData.suggested || false,
                open: typeData.open || false,
                connectionFields: typeData.connectionFields || [],
                isType: true // Flag to indicate this is a software type
              };
              
              types.push(softwareType);
              softwareList.push(softwareType);

              // Also fetch individual software items for this type
              const softwareSnapshot = await getDocs(collection(db, 'technologyStack', collectionName, 'types', typeDoc.id, 'softwares'));
              softwareSnapshot.forEach(softwareDoc => {
                const data = softwareDoc.data();
                if (data && data.name) {
                  softwareItemsList.push({
                    id: softwareDoc.id,
                    name: data.name,
                    typeId: typeDoc.id,
                    typeName: typeData.name,
                    tier: collectionName,
                    description: data.description || '',
                    image: data.image || '',
                    connectionFields: data.connectionFields || []
                  });
                }
              });
            }
          }
          
          collections.push({
            name: collectionName,
            types: types
          });
        }

        // Sort software list by name
        softwareList.sort((a, b) => a.name.localeCompare(b.name));
        setAvailableSoftwares(softwareList);
        console.log('Fetched software types:', softwareList);
        
        // Sort software items list by name
        softwareItemsList.sort((a, b) => a.name.localeCompare(b.name));
        setAvailableSoftwareItems(softwareItemsList);
        console.log('Fetched software items:', softwareItemsList);
        
        // Set software collections
        setSoftwareCollections(collections);
        console.log('Fetched software collections:', collections);
      } catch (error) {
        console.error('Error fetching software list:', error);
      }
    };

    fetchSoftwares();
  }, []);

  // Toggle function for automation status
  const toggleAutomation = async (e, automation) => {
    e.stopPropagation();
    try {
      const automationRef = doc(db, 'automations', automation.id);
      await updateDoc(automationRef, {
        status: automation.status === 'published' ? 'draft' : 'published'
      });

      setAutomations(automations.map(auto => 
        auto.id === automation.id 
          ? { ...auto, status: auto.status === 'published' ? 'draft' : 'published' }
          : auto
      ));
    } catch (error) {
      console.error('Error updating automation status:', error);
    }
  };



  // Handle form submission for new automation request
  const handleRequestSubmit = async () => {
    try {
      // Get user's company information from Firestore document
      let userCompany = 'Unknown Company';
      let userDisplayName = currentUser?.displayName || currentUser?.email || 'Unknown User';
      
      if (currentUser?.uid) {
        try {
          const userDocRef = doc(db, 'users', effectiveUid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const userData = userDoc.data();
            userCompany = userData.company || userData.businessName || 'Unknown Company';
            // In Portal View the effectiveUid is the client's — keep the
            // internal user's own name as the author of an internal add.
            if (!isInternal) {
              userDisplayName = userData.displayName ||
                               (userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : '') ||
                               userData.email ||
                               currentUser?.displayName ||
                               currentUser?.email ||
                               'Unknown User';
            }
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }

      // Create automation request data
      const automationRequest = {
        name: requestForm.name, // Use the provided name
        description: requestForm.description,
        softwares: requestForm.softwares, // Use the selected software types
        emojis: requestForm.emojis, // Use selected emojis
        category: requestForm.department.toLowerCase(), // Convert to lowercase
        requestedBy: userDisplayName,
        company: userCompany,
        workflowSteps: requestForm.workflowSteps.length > 0 ? requestForm.workflowSteps : [
          {
            title: `Initial Step`,
            description: requestForm.description,
            order: 1,
            type: 'trigger'
          }
        ]
      };

      // Save to user's document instead of main automations collection.
      // Internal adds (Portal View) go straight to in-progress and skip the
      // fulfillment notification — there's no request to fulfill.
      const initialStatus = isInternal ? 'in-progress' : 'requested';
      const result = await saveUserAutomationRequest(effectiveUid, automationRequest, {
        accountId: companyId || null,
        skipNotification: isInternal,
        status: initialStatus
      });

      // Add the new automation request to the local state
      const newAutomationRequest = {
        id: result.requestId,
        ...automationRequest,
        status: initialStatus,
        createdAt: new Date().toISOString()
      };
      setUserAutomationRequests(prevRequests => [newAutomationRequest, ...prevRequests]);
      
      setShowRequestModal(false);
      setRequestForm({
        name: '',
        department: 'Lead',
        description: '',
        appsUsed: '',
        softwares: [],
        emojis: [],
        workflowSteps: []
      });
    } catch (error) {
      console.error('Error creating automation request:', error);
      alert('Error submitting automation request. Please try again.');
    }
  };





  // Helper function to get user's automation status
  const getUserAutomationStatus = (automationId) => {
    // Check if this is a user-specific request
    const userRequest = userAutomationRequests.find(req => req.id === automationId);
    if (userRequest) {
      return userRequest.status || 'requested';
    }
    
    // Check template automation status
    if (!currentUser || !userAutomationData[automationId]) {
      return 'off';
    }
    return userAutomationData[automationId].status || 'off';
  };

  // Combine template automations and user requests (both filtered for hidden).
  // Exclude template automations that already have a request (same id) to avoid duplicate cards.
  const requestIds = new Set(userAutomationRequests.map(r => r.id));
  // Overlay any saved per-user/account customizations (name, emoji, description,
  // workflow steps) onto the shared template so edits show on the cards too.
  const applyCustomizations = (item) => {
    const c = userAutomationData[item.id]?.customizations;
    if (!c) return item;
    return {
      ...item,
      ...(c.name != null ? { name: c.name } : {}),
      ...(c.description != null ? { description: c.description } : {}),
      ...(Array.isArray(c.emojis) ? { emojis: c.emojis } : {}),
      ...(Array.isArray(c.workflowSteps) ? { workflowSteps: c.workflowSteps } : {}),
    };
  };

  const allAutomations = [
    ...automations.filter(item => {
      if (requestIds.has(item.id)) return false;
      return getUserAutomationStatus(item.id) !== 'hidden';
    }).map(applyCustomizations),
    ...userAutomationRequests.filter(item => item.status !== 'hidden'),
  ];

  // Departments become jobtread-imports-style sections: the dropdown narrows
  // to one department and the search filters cards within each section.
  const visibleSections = useMemo(() => {
    const searched = filterResourcesBySearch(allAutomations, searchTerm, ['name', 'description', 'authorName', 'appsUsed']);
    return DEPARTMENT_SECTIONS
      .filter((section) => activeTab === 'all' || section.id === activeTab)
      .map((section) => ({ ...section, items: searched.filter((item) => (item.category || 'lead') === section.id) }))
      .filter((section) => section.items.length > 0);
  }, [allAutomations, searchTerm, activeTab]);

  // Close the details popup and refresh statuses so any request/change made
  // inside it is reflected on the cards right away.
  const handleCloseOverview = async () => {
    setSelectedAutomationId(null);
    if (!currentUser) return;
    try {
      const [userData, requests] = await Promise.all([
        getAllUserAutomationData(effectiveUid, companyId || null),
        getUserAutomationRequests(effectiveUid, companyId || null),
      ]);
      setUserAutomationData(userData);
      setUserAutomationRequests(requests);
    } catch (error) {
      console.error('Error refreshing automation data:', error);
    }
  };

  // Handle app connection
  const handleConnectApp = (softwareId) => {
    const software = availableSoftwares.find(s => s.id === softwareId);
    if (software) {
      setSelectedApp({
        id: software.id,
        name: software.name,
        icon: <Zap size={24} className="text-blue-500" />,
        description: software.description,
        fields: software.connectionFields || []
      });
      setShowAppLoginModal(true);
    }
  };

  // Handle opening Connect Apps modal
  const handleOpenConnectAppsModal = () => {
    // Refresh selected apps from localStorage in case they were updated in AutomationTemplates
    setShowConnectAppsModal(true);
  };

  // Refresh app connections data
  const refreshAppConnections = () => {
    // Force a re-render by updating state
    setDataLoaded(false);
    setTimeout(() => setDataLoaded(true), 100);
    
    // Also check localStorage directly
    const savedApps = localStorage.getItem('appConnections');
    console.log('Refreshing app connections, current localStorage:', savedApps);
  };

  // Handle app credentials submission
  const handleAppCredentialsSubmit = async () => {
    if (!currentUser || !selectedApp) return;
    
    try {
      // Save the connection data to user's app connections
      const connectionData = {
        softwareId: selectedApp.id,
        softwareName: selectedApp.name,
        fields: appCredentials[selectedApp.id] || {},
        connectedAt: new Date().toISOString()
      };
      
      // Save to user's document in users collection
      const userRef = doc(db, 'users', effectiveUid);
      
      // Get current user data
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const currentConnections = userData.appConnections || {};
        
        // Update the specific connection
        currentConnections[selectedApp.id] = connectionData;
        
        // Update the user document
        await updateDoc(userRef, {
          appConnections: currentConnections,
          updatedAt: new Date().toISOString()
        });
        
        console.log('Successfully saved app connection to user document');
      }
      
      // Update local state
      setConnectedApps({
        ...connectedApps,
        [selectedApp.id]: true
      });
      
      setUserAppConnections({
        ...userAppConnections,
        [selectedApp.id]: connectionData
      });
      
      setShowAppLoginModal(false);
      setSelectedApp(null);
    } catch (error) {
      console.error('Error connecting app:', error);
    }
  };

  // Handle showing connection fields
  const handleShowConnectionFields = (software) => {
    setSelectedConnectionFields(software);
    setShowConnectionFieldsModal(true);
    
    // Load existing field values if they exist
    if (userAppConnections[software.id] && userAppConnections[software.id].fieldValues) {
      setConnectionFieldValues(userAppConnections[software.id].fieldValues);
    } else {
      // Initialize with empty values
      const initialValues = {};
      if (software.connectionFields) {
        software.connectionFields.forEach(field => {
          initialValues[field.fieldName] = '';
        });
      }
      setConnectionFieldValues(initialValues);
    }
  };

  // Handle saving connection field values
  const handleSaveConnectionFields = async () => {
    if (!currentUser || !selectedConnectionFields) return;
    
    try {
      setIsSavingFields(true);
      
      // Create or update the user's app connection with field values
      const connectionData = {
        softwareId: selectedConnectionFields.id,
        softwareName: selectedConnectionFields.name,
        fields: {}, // Keep existing fields if any
        fieldValues: connectionFieldValues,
        connectedAt: userAppConnections[selectedConnectionFields.id]?.connectedAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      // Save to user's document in users collection
      const userRef = doc(db, 'users', effectiveUid);
      
      console.log('Saving connection fields for software:', selectedConnectionFields.id);
      console.log('Field values to save:', connectionFieldValues);
      
      // Get current user data
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const currentConnections = userData.appConnections || {};
        
        console.log('Current user connections:', currentConnections);
        
        // Update the specific connection
        currentConnections[selectedConnectionFields.id] = connectionData;
        
        console.log('Updated connections object:', currentConnections);
        
        // Update the user document
        await updateDoc(userRef, {
          appConnections: currentConnections,
          updatedAt: new Date().toISOString()
        });
        
        console.log('Successfully saved connection fields to user document');
      } else {
        console.error('User document does not exist');
      }
      
      // Update local state
      setUserAppConnections({
        ...userAppConnections,
        [selectedConnectionFields.id]: connectionData
      });
      
      // Also update connectedApps state
      setConnectedApps({
        ...connectedApps,
        [selectedConnectionFields.id]: true
      });
      
      // Show success feedback
      setShowConnectionFieldsModal(false);
      setSelectedConnectionFields(null);
      setConnectionFieldValues({});
      
    } catch (error) {
      console.error('Error saving connection fields:', error);
    } finally {
      setIsSavingFields(false);
    }
  };

  // Handle app disconnection
  const handleDisconnectApp = async (softwareId) => {
    if (!currentUser) return;
    
    try {
      await deleteUserAppConnection(effectiveUid, softwareId);
      
      // Update local state
      const newConnectedApps = { ...connectedApps };
      delete newConnectedApps[softwareId];
      setConnectedApps(newConnectedApps);
      
      const newUserAppConnections = { ...userAppConnections };
      delete newUserAppConnections[softwareId];
      setUserAppConnections(newUserAppConnections);
      
      const newAppCredentials = { ...appCredentials };
      delete newAppCredentials[softwareId];
      setAppCredentials(newAppCredentials);
    } catch (error) {
      console.error('Error disconnecting app:', error);
    }
  };

  // Handle edit submission
  const handleEditSubmit = async () => {
    try {
      const automationRef = doc(db, 'automations', editingAutomation.id);
      const appsUsed = requestForm.appsUsed ? requestForm.appsUsed.split(',').map(app => app.trim()) : [];
      
      await updateDoc(automationRef, {
        name: requestForm.trigger,
        description: requestForm.description,
        softwares: appsUsed,
        category: requestForm.department.toLowerCase()
      });

      setAutomations(automations.map(auto => 
        auto.id === editingAutomation.id 
          ? { 
              ...auto, 
              name: requestForm.trigger,
              description: requestForm.description,
              softwares: appsUsed,
              category: requestForm.department.toLowerCase()
            }
          : auto
      ));

      setShowRequestModal(false);
      setEditingAutomation(null);
      setRequestForm({
        department: 'Lead',
        description: '',
        appsUsed: '',
        trigger: ''
      });
    } catch (error) {
      console.error('Error updating automation:', error);
    }
  };

  if (loading) {
    return (
      <div className={`p-6 flex items-center justify-center`}>
        <div className="text-center">
          <BBALoader size={48} className="mx-auto" />
          <p className={`mt-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading automations...</p>
        </div>
      </div>
    );
  }

  const inputBase = `rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
    darkMode
      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400'
      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
  }`;

  return (
    <div className={embedded ? '' : 'min-h-screen p-6 relative'}>
      {!embedded && <PortalBackground />}
      <div className={embedded ? '' : 'relative'}>
      {!embedded && <TechnologyPageHeader title="Automations" />}

      {/* Search + department filter (jobtread-imports style) */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search
            className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
              darkMode ? 'text-gray-500' : 'text-gray-400'
            }`}
          />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search automations..."
            className={`w-full pl-9 pr-3 py-2 ${inputBase}`}
          />
        </div>
        <select
          value={activeTab}
          onChange={(e) => setActiveTab(e.target.value)}
          className={`sm:w-56 px-3 py-2 ${inputBase}`}
        >
          <option value="all">All Departments</option>
          {DEPARTMENT_SECTIONS.map((section) => (
            <option key={section.id} value={section.id}>
              {section.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setShowRequestModal(true)}
          className="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors shrink-0"
        >
          <Plus className="w-4 h-4" />
          {isInternal ? 'Add New' : 'Request Custom'}
        </button>
      </div>

      {visibleSections.length === 0 ? (
        <div
          className={`rounded-xl border p-8 text-center ${
            darkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'
          }`}
        >
          <Zap className={`w-8 h-8 mx-auto mb-3 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
          <h3 className={`text-base font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {allAutomations.length === 0 ? 'No automations yet' : 'No automations match your search'}
          </h3>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {allAutomations.length === 0
              ? 'Share a template or request a custom automation to get started.'
              : 'Try a different search or department.'}
          </p>
        </div>
      ) : (
        visibleSections.map((section) => {
          return (
            <div key={section.id} className="mt-10 first:mt-0">
              <div className="flex items-center justify-center gap-3 mb-5">
                <span className={`h-px flex-1 max-w-[110px] ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                <span className="shrink-0 w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
                  <span className="text-xl leading-none">{groupEmojis[section.id] || section.emoji}</span>
                </span>
                <h2 className={`text-xl md:text-2xl font-bold tracking-tight leading-tight text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {section.label}
                </h2>
                <span className={`h-px flex-1 max-w-[110px] ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {section.items.map((automation) => {
                  const status =
                    requestIds.has(automation.id) && automation.status != null
                      ? automation.status
                      : getUserAutomationStatus(automation.id);

                  return (
                    <div
                      key={automation.id}
                      id={`automation-card-${automation.id}`}
                      className={`rounded-xl transition-shadow ${
                        highlightId === automation.id ? 'ring-2 ring-emerald-500 shadow-lg' : ''
                      }`}
                    >
                      <TechnologyAutomationCard
                        automation={automation}
                        status={status}
                        onClick={() => setSelectedAutomationId(automation.id)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}



      {/* Request Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-lg w-full`}>
            {/* Modal Header */}
            <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} p-3 flex justify-between items-center`}>
              <h2 className={`text-lg font-bold flex-1 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {editingAutomation ? 'Edit Automation' : isInternal ? 'Add New Automation' : 'Request New Automation'}
              </h2>
              <button 
                onClick={() => {
                  setShowRequestModal(false);
                  setEditingAutomation(null);
                }}
                className={`rounded-full p-1 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
              >
                <X size={20} className={darkMode ? 'text-gray-300' : 'text-gray-600'} />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-4">
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Name *</label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white'} rounded-md text-sm`}
                    value={requestForm.name}
                    onChange={(e) => setRequestForm({ ...requestForm, name: e.target.value })}
                    placeholder="Enter automation name"
                    required
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Department</label>
                  <select
                    className={`w-full px-3 py-2 border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white'} rounded-md text-sm`}
                    value={requestForm.department}
                    onChange={(e) => setRequestForm({ ...requestForm, department: e.target.value })}
                  >
                    <option value="Lead">Lead</option>
                    <option value="Design">Design</option>
                    <option value="Build">Build</option>
                    <option value="Office">Office</option>
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Description *</label>
                  <textarea
                    className={`w-full px-3 py-2 border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white'} rounded-md text-sm`}
                    rows={4}
                    value={requestForm.description}
                    onChange={(e) => setRequestForm({ ...requestForm, description: e.target.value })}
                    placeholder="Describe what this automation should do..."
                    required
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} p-4 flex justify-end space-x-3`}>
              <button
                onClick={() => {
                  setShowRequestModal(false);
                  setEditingAutomation(null);
                  setRequestForm({
                    name: '',
                    department: 'Lead',
                    description: '',
                    appsUsed: '',
                    softwares: [],
                    emojis: [],
                    workflowSteps: []
                  });
                }}
                className={`px-3 py-1 ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} rounded-md text-sm`}
              >
                Cancel
              </button>
              <button
                onClick={editingAutomation ? handleEditSubmit : handleRequestSubmit}
                className="px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm"
              >
                {editingAutomation ? 'Save Changes' : isInternal ? 'Add Automation' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Connect Apps Modal */}
      {showConnectAppsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-lg w-full`}>
            {/* Modal Header */}
            <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} p-3 flex justify-between items-center`}>
              <div className="flex-1 text-center">
                <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Connect Apps</h2>
              </div>
              <button 
                onClick={() => setShowConnectAppsModal(false)}
                className={`rounded-full p-1 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
              >
                <X size={20} className={darkMode ? 'text-gray-300' : 'text-gray-600'} />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-4">
              
              {!dataLoaded ? (
                <div className="text-center py-8">
                  <BBALoader size={32} className="mx-auto mb-4" />
                  <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Loading software integrations...
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {getSelectedApps().map((software) => (
                    <div
                      key={software.id}
                      className={`border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-xl overflow-hidden`}
                    >
                      {/* App Header */}
                      <div className="flex items-center justify-between p-3">
                        <div className="flex items-center gap-3 flex-1">
                          {software.isUserSelection && software.image ? (
                            <img 
                              src={software.image} 
                              alt={`${software.name} logo`}
                              className="h-6 w-auto object-contain"
                            />
                          ) : software.isUserSelection ? (
                            <div className={`h-6 w-6 rounded ${darkMode ? 'bg-gray-600' : 'bg-gray-200'} flex items-center justify-center`}>
                              <Zap size={16} className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
                            </div>
                          ) : (
                            <div className="h-6 w-6 rounded-full bg-red-500 flex items-center justify-center">
                              <X size={16} className="text-white" />
                            </div>
                          )}
                          <div className="flex-1">
                            <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {software.isUserSelection ? software.name : software.name}
                            </h3>
                            {software.isUserSelection && (
                              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {software.type} • {software.tier}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Connection Fields Button */}
                          {software.connectionFields && software.connectionFields.length > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShowConnectionFields(software);
                              }}
                              className={`px-2 py-1 text-xs rounded ${
                                userAppConnections[software.id]?.fieldValues 
                                  ? `${darkMode ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'}` 
                                  : `${darkMode ? 'bg-red-600 hover:bg-red-700' : 'bg-red-500 hover:bg-red-600'}`
                              } text-white`}
                            >
                              {userAppConnections[software.id]?.fieldValues ? 'Edit Fields' : 'View Fields'}
                            </button>
                          )}
                          {/* Show green check only for specific software selections that are connected */}
                          {software.isUserSelection && connectedApps[software.id] ? (
                            <Check size={20} className="text-green-500" />
                          ) : (
                            <X size={20} className="text-red-500" />
                          )}
                        </div>
                      </div>
                      

                    </div>
                  ))}
                  {getSelectedApps().length === 0 && (
                    <div className="text-center py-8">
                      <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        No apps selected in automation templates yet.
                      </p>
                      <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'} mt-2 mb-4`}>
                        Go to Automation Templates and select apps to connect them here.
                      </p>
                      <button
                        onClick={() => navigate('/crm/automation-templates')}
                        className={`px-4 py-2 rounded-md ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white text-sm`}
                      >
                        Go to Automation Templates
                      </button>
                      <div className="mt-4">
                        <button
                          onClick={refreshAppConnections}
                          className={`px-3 py-1 text-xs rounded ${darkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-200 hover:bg-gray-300'} ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}
                        >
                          Refresh
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* App Login Modal */}
      {showAppLoginModal && selectedApp && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-lg w-full`}>
            {/* Modal Header */}
            <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} p-3 flex justify-between items-center`}>
              <div className="flex items-center gap-2">
                {selectedApp.icon}
                <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Connect {selectedApp.name}</h2>
              </div>
              <button 
                onClick={() => {
                  setShowAppLoginModal(false);
                  setSelectedApp(null);
                }}
                className={`rounded-full p-1 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
              >
                <X size={20} className={darkMode ? 'text-gray-300' : 'text-gray-600'} />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-4">
              <div className="space-y-4">
                {selectedApp.fields && selectedApp.fields.length > 0 ? (
                  selectedApp.fields.map((field) => (
                    <div key={field.fieldName || field.id}>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {field.fieldName}
                      </label>
                      <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-2`}>
                        {field.fieldDescription}
                      </p>
                      <input
                        type="text"
                        className={`w-full px-3 py-2 border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white'} rounded-md text-sm`}
                        value={appCredentials[selectedApp.id]?.[field.fieldName] || ''}
                        onChange={(e) => setAppCredentials({
                          ...appCredentials,
                          [selectedApp.id]: {
                            ...appCredentials[selectedApp.id],
                            [field.fieldName]: e.target.value
                          }
                        })}
                        placeholder={`Enter ${field.fieldName.toLowerCase()}`}
                      />
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      No connection fields configured for this software.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} p-4 flex justify-end space-x-3`}>
              <button
                onClick={() => {
                  setShowAppLoginModal(false);
                  setSelectedApp(null);
                }}
                className={`px-3 py-1 ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} rounded-md text-sm`}
              >
                Cancel
              </button>
              <button
                onClick={handleAppCredentialsSubmit}
                className="px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm"
              >
                Connect
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Connection Fields Modal */}
      {showConnectionFieldsModal && selectedConnectionFields && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-lg w-full`}>
            {/* Modal Header */}
            <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} p-3 flex justify-between items-center`}>
              <div className="flex items-center gap-2">
                {selectedConnectionFields.image ? (
                  <img 
                    src={selectedConnectionFields.image} 
                    alt={`${selectedConnectionFields.name} logo`}
                    className="h-6 w-auto object-contain"
                  />
                ) : (
                  <div className={`h-6 w-6 rounded ${darkMode ? 'bg-gray-600' : 'bg-gray-200'} flex items-center justify-center`}>
                    <Zap size={16} className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
                  </div>
                )}
                <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Connection Fields: {selectedConnectionFields.name}
                </h2>
              </div>
              <button 
                onClick={() => {
                  setShowConnectionFieldsModal(false);
                  setSelectedConnectionFields(null);
                  setConnectionFieldValues({});
                }}
                className={`rounded-full p-1 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
              >
                <X size={20} className={darkMode ? 'text-gray-300' : 'text-gray-600'} />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-4">
              <div className="space-y-4">
                {selectedConnectionFields.connectionFields && selectedConnectionFields.connectionFields.length > 0 ? (
                  selectedConnectionFields.connectionFields.map((field, index) => (
                    <div key={index} className={`p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}>
                      <div className={`font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {field.fieldName}
                      </div>
                      <div className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {field.fieldDescription}
                      </div>
                      <input
                        type="text"
                        className={`w-full px-3 py-2 border rounded-md text-sm ${
                          darkMode 
                            ? 'border-gray-600 bg-gray-800 text-white focus:border-red-500' 
                            : 'border-gray-300 bg-white text-gray-900 focus:border-red-500'
                        } focus:outline-none focus:ring-1 focus:ring-red-500`}
                        value={connectionFieldValues[field.fieldName] || ''}
                        onChange={(e) => setConnectionFieldValues({
                          ...connectionFieldValues,
                          [field.fieldName]: e.target.value
                        })}
                        placeholder={`Enter your ${field.fieldName.toLowerCase()}`}
                      />
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Choose an option in Tech Stack to see connection fields.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} p-4 flex justify-end space-x-3`}>
              <button
                onClick={() => {
                  setShowConnectionFieldsModal(false);
                  setSelectedConnectionFields(null);
                  setConnectionFieldValues({});
                }}
                className={`px-4 py-2 ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} rounded-md text-sm`}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveConnectionFields}
                disabled={isSavingFields}
                className={`px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center gap-2`}
              >
                {isSavingFields ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  'Save Fields'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Automation Details Popup — full overview (status, workflow steps, request, customize) in a large modal */}
      {selectedAutomationId && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={handleCloseOverview}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-y-auto`}
          >
            <AutomationOverview
              automationId={selectedAutomationId}
              onClose={handleCloseOverview}
              overrideUserId={overrideUserId}
              overrideCompanyId={overrideCompanyId}
            />
          </div>
        </div>
      )}

      </div>
    </div>
  );
};

export default Automation; 